/** @format */
import React, { forwardRef, memo, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useGlobalState } from "senf-shared/globalState/GlobalState";
import styled from "styled-components";
import { tv } from "tailwind-variants";
import { cn } from "../../../util/classNameConstructor";
import Icon from "../../core/icon/Icon";
import type { ImageInput } from "../../special/imageModifier/ImagesUploadModal";
import ModifyImageButtons from "../../special/imageModifier/ModifyImageButtons";
import { CopyrightIcon } from "../../special/survey/components/SurveyImages";
import type { SurveyImage } from "../../special/survey/survey.types";
import type { DraggableImagesProps } from "./DraggableImages";

const DraggableImageStyles = tv({
  slots: {
    item: [
      "group/item p-2 [&.react-draggable]:hover:cursor-move",
      "[&.react-draggable-dragging]:z-10 [&.react-draggable-dragging]:duration-0",
      "[&.resizing]:z-10 [&.resizing]:duration-0 [&.resizing]:cursor-nwse-resize",
      "[&~.react-grid-placeholder]:rounded-3xl [&.react-draggable-dragging~.react-grid-placeholder]:bg-neutral-900/10",
    ],
    image: [
      "pointer-events-none h-full w-full overflow-hidden rounded-2xl object-cover duration-200",
      "[.react-draggable-dragging_&]:shadow-2xl",
    ],
    buttonContainer: "absolute inset-0 m-3",
    button: "opacity-0 group-hover/item:opacity-100",
  },
  variants: {
    mode: {
      edit: {
        item: "duration-500 ease-expressive-out [&~.react-grid-placeholder]:duration-500",
      },
      display: {},
    },
  },
});

interface ImageProps
  extends SurveyImage,
    Omit<
      DraggableImagesProps,
      "layout" | "updateQuestion" | "updateLayout" | "onHeightChange"
    >,
    Pick<React.HTMLAttributes<HTMLDivElement>, "children" | "className"> {
  index: number;
  i: string;
  url: string;
  alt?: string;
  removeImage: (i: string) => void;
  updateImage?: (i: string, updates: Partial<SurveyImage>) => void;
  onImageClick?: (index: number) => void;
  handleFileChange?: (files: ImageInput[], i: string) => Promise<void>;
  imageFocus?: number | false;
  imgFormat?: boolean;
  onFormatChange?: (value: boolean) => void;
  copyright?: string;
  project?: any;
}

const ItemWrappper = styled.div`
  .react-resizable-handle {
    cursor: nwse-resize;
    position: absolute;
    bottom: 0;
    right: 0;
    display: block;
    border-radius: 0 0 10px 0;
    border: 2px solid white;
    border-style: none solid solid none;
    width: 1rem;
    height: 1rem;
    margin: 2px;
  }
`;

const Item = forwardRef<HTMLDivElement, ImageProps>(
  (
    {
      project,
      i,
      index,
      url,
      alt,
      removeImage,
      updateImage,
      onImageClick,
      handleFileChange,
      mode,
      children,
      className,
      imgFormat,
      onFormatChange,
      copyright,
      surveyId,
      imageMetadata,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const [translation] = useGlobalState().translationsState;

    const imageRef = useRef<HTMLImageElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const { item, image, buttonContainer, button } = DraggableImageStyles({
      mode,
    });

    const handleImageChange = useCallback(
      async (filesOrImages: ImageInput[]) => {
        if (handleFileChange && filesOrImages.length > 0) {
          return handleFileChange([filesOrImages[0]], i);
        }
      },
      [handleFileChange, i],
    );

    const dragHandle = React.useMemo(() => children, [children]);

    const allowedFileTypes = [
      "image/png",
      "image/jpeg",
      "image/jpg",
      "image/webp",
    ];

    return (
      <ItemWrappper
        ref={ref}
        {...props}
        className={cn(
          item({ className }),
          "group/option",
          typeof props.imageFocus === "number" &&
            props.imageFocus !== index &&
            "opacity-30 blur-sm",
        )}
      >
        {url ? (
          <>
            <img
              ref={imageRef}
              src={url}
              className={cn(image())}
              alt={
                translation?.[alt] ||
                alt ||
                imageMetadata[url]?.customMetadata?.alt ||
                t("global:image")
              }
            />
            {mode === "display" && copyright && (
              <CopyrightIcon copyright={copyright} />
            )}
            {/* {mode === "edit" && (
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files?.length) {
                    handleFileChange?.(e.target.files, index);
                  }
                }}
              />
            )} */}
          </>
        ) : (
          <div className="bg-surface-subdued flex h-full w-full items-center justify-center rounded-2xl">
            <Icon name="loading" />
          </div>
        )}
        {url && mode === "edit" && (
          <div className={buttonContainer()}>
            <ModifyImageButtons
              project={project}
              surveyId={surveyId}
              inputRef={inputRef}
              imageUrl={url}
              altText={alt}
              altTextPlaceholder={imageMetadata[url]?.customMetadata?.alt}
              copyright={copyright}
              onChangeImage={handleImageChange}
              onRemoveImage={() => removeImage(i)}
              onAltTextChange={(value) => {
                updateImage?.(i, { alt: value });
              }}
              onCopyrightChange={(value) => {
                updateImage?.(i, { copyright: value });
              }}
              onFormatChange={onFormatChange}
              imgFormat={imgFormat}
              maxItems={1}
              saveToPath={`surveys/${surveyId}`}
              fileTypes={allowedFileTypes}
            />
            <div className={button()}>{dragHandle}</div>
          </div>
        )}
      </ItemWrappper>
    );
  },
);

Item.displayName = "Item";
export default memo(Item);
